import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { AlertTriangle, ShieldAlert, Trash2 } from 'lucide-react';
import { Modal } from './Modal';

export interface ConfirmOptions {
  title: string;
  description?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: 'default' | 'danger' | 'warning';
  /** Require typing the model name / "DELETE" to enable confirm — guards mass actions. */
  requireType?: string;
}

interface ConfirmCtxValue {
  confirm: (opts: ConfirmOptions) => Promise<boolean>;
}

const ConfirmContext = createContext<ConfirmCtxValue | null>(null);

interface PendingConfirm {
  opts: ConfirmOptions;
  resolve: (ok: boolean) => void;
}

export const ConfirmProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [pending, setPending] = useState<PendingConfirm | null>(null);
  const [typed, setTyped] = useState('');
  const resolveRef = useRef<(ok: boolean) => void>(() => {});

  const confirm = useCallback((opts: ConfirmOptions) => {
    return new Promise<boolean>(resolve => {
      resolveRef.current = resolve;
      setTyped('');
      setPending({ opts, resolve });
    });
  }, []);

  const handleClose = useCallback((ok: boolean) => {
    if (pending) pending.resolve(ok);
    setPending(null);
    setTyped('');
  }, [pending]);

  const value = useMemo(() => ({ confirm }), [confirm]);

  const opts = pending?.opts;
  const tone = opts?.tone ?? 'default';
  const typeGuard = opts?.requireType ?? '';
  const typedMatch = typeGuard ? typed.trim() === typeGuard : true;
  const Icon = tone === 'danger' ? Trash2 : tone === 'warning' ? AlertTriangle : ShieldAlert;
  const iconTone =
    tone === 'danger' ? 'var(--danger)'
      : tone === 'warning' ? 'var(--warning)'
        : 'var(--accent)';

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      <Modal
        isOpen={pending !== null}
        onClose={() => handleClose(false)}
        title={opts?.title ?? ''}
        size="sm"
        footer={
          <>
            <button
              type="button"
              className="hf-btn ghost"
              onClick={() => handleClose(false)}
            >
              {opts?.cancelLabel ?? 'Cancel'}
            </button>
            <button
              type="button"
              className={`hf-btn ${tone === 'danger' ? 'danger' : 'primary'}`}
              onClick={() => handleClose(true)}
              disabled={!typedMatch}
              autoFocus={!typeGuard}
            >
              {opts?.confirmLabel ?? 'Confirm'}
            </button>
          </>
        }
      >
        <div className="hf-confirm-body">
          <span className="hf-confirm-icon" style={{ color: iconTone }} aria-hidden="true">
            <Icon size={22} strokeWidth={1.8} />
          </span>
          <div className="hf-confirm-text">
            {opts?.description && (
              <div className="hf-confirm-desc">{opts.description}</div>
            )}
            {typeGuard && (
              <label className="hf-confirm-type">
                <span>
                  Type <code className="hf-mono">{typeGuard}</code> to confirm
                </span>
                <input
                  className="hf-textarea"
                  style={{ minHeight: 34, padding: '6px 10px' }}
                  value={typed}
                  onChange={e => setTyped(e.target.value)}
                  autoFocus
                  spellCheck={false}
                  autoComplete="off"
                />
              </label>
            )}
          </div>
        </div>
      </Modal>
    </ConfirmContext.Provider>
  );
};

export function useConfirm(): (opts: ConfirmOptions) => Promise<boolean> {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    // Fallback for trees not wrapped in provider (early boot, tests).
    return async opts => window.confirm(`${opts.title}\n\n${typeof opts.description === 'string' ? opts.description : ''}`);
  }
  return ctx.confirm;
}
